  import React, { useEffect, useState } from 'react';
import apiClient from '../apiClient';

interface PagedResponse<T> {
  data: {
    items: T[];
    total: number;
  };
}

interface ActivityItem {
  id: number;
  type: string;
  user: { id: number; username: string; role: string } | null;
  data: any;
  created_at: number;
}

interface ScheduleItem {
  id: number;
  project_id: number;
  project_name?: string | null;
  auditor_id: number;
  auditor_username?: string | null;
  scheduled_start: string; // ISO parcial para el input datetime-local (YYYY-MM-DDTHH:MM)
  scheduled_end: string; // igual que arriba
  status: string;
  risk_score?: number | null;
}

interface ProjectOption {
  id: number;
  name: string;
}

interface UserOption {
  id: number;
  username: string;
}

interface TemplateOption {
  id: number;
  name: string;
  category: string;
  project_id: number | null;
}

interface EvidenceItem {
  id: number;
  schedule_id: number | null;
  project_id: number | null;
  uploaded_by: number;
  original_name: string;
  mime_type: string | null;
  size: number | null;
  processed_at: number | null;
  processing_attempts: number;
  last_error: string | null;
  url: string;
}

interface SchedulePdfItem {
  id: number;
  schedule_id: number;
  s3_key: string;
  created_at: number;
  created_by: number;
  url: string;
}

interface ChecklistItem {
  id: number;
  label: string;
  is_required: boolean;
  position: number;
  completed_at: number | null;
  completed_by: number | null;
  note: string | null;
}

const emptyForm: {
  id: number | null;
  project_id: number | '';
  template_id: number | '';
  auditor_id: number | '';
  scheduled_start: string;
  scheduled_end: string;
  status: string;
} = {
  id: null,
  project_id: '',
   template_id: '',
  auditor_id: '',
  scheduled_start: '',
  scheduled_end: '',
  status: 'pending',
};

const SchedulesPage: React.FC = () => {
  const [items, setItems] = useState<ScheduleItem[]>([]);
  const [form, setForm] = useState<typeof emptyForm>(emptyForm);
  const [onlyMine, setOnlyMine] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(null);
  const [evidences, setEvidences] = useState<EvidenceItem[]>([]);
  const [evidencesLoading, setEvidencesLoading] = useState(false);
  const [evidencesError, setEvidencesError] = useState<string | null>(null);
  const [evidenceStats, setEvidenceStats] = useState<{ total: number; pending: number; error: number; processed: number } | null>(null);
  const [pdfs, setPdfs] = useState<SchedulePdfItem[]>([]);
  const [pdfsLoading, setPdfsLoading] = useState(false);
  const [pdfsError, setPdfsError] = useState<string | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [evidencesFilter, setEvidencesFilter] = useState<'all' | 'pending' | 'error'>('all');
  const [auditorSuggestions, setAuditorSuggestions] = useState<
    { id: number; username: string; email: string; currentLoad: number; score: number }[]
  >([]);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfMessage, setPdfMessage] = useState<string | null>(null);
  const [lastPresenceScheduleId, setLastPresenceScheduleId] = useState<number | null>(null);
  const [presenceUsers, setPresenceUsers] = useState<{ id: number; username: string; role: string }[]>([]);
  const [activityItems, setActivityItems] = useState<ActivityItem[]>([]);
  const [activityFilter, setActivityFilter] = useState({ checklist: true, evidences: true, audit: true });
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistError, setChecklistError] = useState<string | null>(null);
  const [newChecklistLabel, setNewChecklistLabel] = useState('');
  const [newChecklistRequired, setNewChecklistRequired] = useState(false);
  const [newChecklistPosition, setNewChecklistPosition] = useState<number | ''>('');

  // Cargar actividad reciente de la auditoría seleccionada
  useEffect(() => {
    if (!selectedScheduleId) {
      setActivityItems([]);
      return;
    }

    let cancelled = false;

    const loadActivity = async () => {
      try {
        const res = await apiClient.get<{
          data: { items: ActivityItem[] };
        }>('/realtime/activity', {
          params: { schedule_id: selectedScheduleId },
        });
        if (!cancelled) {
          setActivityItems(res.data.data.items || []);
        }
      } catch {
        if (!cancelled) {
          setActivityItems([]);
        }
      }
    };

    loadActivity();
    const interval = window.setInterval(loadActivity, 30000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [selectedScheduleId]);

  const formatRelativeTime = (timestampSeconds: number): string => {
    const now = Date.now();
    const diffMs = now - timestampSeconds * 1000;
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return 'hace unos segundos';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `hace ${diffMin} min`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `hace ${diffHours} h`;
    const diffDays = Math.floor(diffHours / 24);
    return `hace ${diffDays} d`;
  };

  const formatActivityMessage = (item: ActivityItem): string => {
    const username = item.user?.username ?? 'Alguien';
    const t = item.type;
    if (t === 'checklist_item_completed') {
      const label = item.data?.label ?? '';
      return `${username} completó el ítem de checklist "${label}".`;
    }
    if (t === 'evidence_uploaded') {
      const name = item.data?.original_name ?? '';
      return `${username} subió la evidencia "${name}".`;
    }
    if (t === 'evidence_error') {
      const name = item.data?.original_name ?? '';
      return `⚠ ${username} tiene una evidencia con error en "${name}".`;
    }
    if (t === 'evidence_processed') {
      const name = item.data?.original_name ?? '';
      return `${username} tiene una evidencia procesada: "${name}".`;
    }
    if (t === 'audit_completed') {
      return `${username} marcó la auditoría como completada.`;
    }
    return `${username} realizó una acción (${t}).`;
  };

  const loadSchedules = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = onlyMine ? '/schedules/my-agenda' : '/schedules';
      const res = await apiClient.get<PagedResponse<any>>(url, {
        params: { per_page: 50 },
      });

      const loadedItems: ScheduleItem[] = (res.data.data.items || []).map((raw: any) => {
        const date: string | null = raw.scheduled_date ?? null;
        const startTime: string | null = raw.scheduled_time_start ?? null;
        const endTime: string | null = raw.scheduled_time_end ?? null;

        const toLocalInput = (d: string | null, t: string | null): string => {
          if (!d) return '';
          if (!t) return `${d}T00:00`;
          // t viene como HH:MM:SS, nos quedamos con HH:MM
          return `${d}T${t.slice(0, 5)}`;
        };

        return {
          id: raw.id,
          project_id: raw.project_id,
          project_name: raw.project_name ?? null,
          auditor_id: raw.auditor_id,
          auditor_username: raw.auditor_username ?? null,
          scheduled_start: toLocalInput(date, startTime),
          scheduled_end: toLocalInput(date, endTime),
          status: raw.status,
          risk_score: raw.risk_score ?? null,
        };
      });

      setItems(loadedItems);

      // Si venimos del dashboard con ?scheduleId=, intentamos seleccionar esa auditoría
      try {
        const params = new URLSearchParams(window.location.search);
        const scheduleIdParam = params.get('scheduleId');
        const scheduleId = scheduleIdParam ? parseInt(scheduleIdParam, 10) : NaN;
        if (!Number.isNaN(scheduleId)) {
          const found = loadedItems.find(it => it.id === scheduleId);
          if (found) {
            // Seleccionamos la auditoría para evidencias
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            selectScheduleForEvidences(found);
          }
        }
      } catch {
        // Ignoramos errores al leer parámetros de la URL
      }
    } catch (e) {
      setError('No se pudieron cargar las auditorías');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!selectedScheduleId) return;
    setPdfLoading(true);
    setPdfMessage(null);
    try {
      await apiClient.post(`/schedules/${selectedScheduleId}/generate-pdf`);
      setPdfMessage('PDF encolado para generación. Recibirás una notificación cuando esté listo.');
      // Intentamos refrescar la lista de PDFs, aunque el PDF aún pueda no estar generado
      try {
        setPdfsLoading(true);
        const pdfRes = await apiClient.get<{ data: { items: SchedulePdfItem[] } }>(
          `/schedules/${selectedScheduleId}/pdfs`
        );
        setPdfs(pdfRes.data.data.items);
        setPdfsError(null);
      } catch (e) {
        // Silencioso: puede que aún no haya PDFs
      } finally {
        setPdfsLoading(false);
      }
    } catch (e) {
      setPdfMessage('No se pudo encolar la generación del PDF.');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    setUploadFile(file ?? null);
  };

  const handleUploadEvidence = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedScheduleId || !uploadFile) return;

    setUploadLoading(true);
    setEvidencesError(null);

    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('schedule_id', String(selectedScheduleId));

    try {
      const res = await apiClient.post<{ data: EvidenceItem }>(
        '/evidences/upload',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      setEvidences(prev => [res.data.data, ...prev]);
      setUploadFile(null);
    } catch (e) {
      setEvidencesError('Error al subir la evidencia');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDeleteEvidence = async (id: number) => {
    if (!window.confirm('¿Eliminar esta evidencia?')) return;

    try {
      await apiClient.delete(`/evidences/${id}`);
      setEvidences(prev => prev.filter(ev => ev.id !== id));
    } catch (e) {
      setEvidencesError('No se pudo eliminar la evidencia');
    }
  };

  const handleReprocessEvidence = async (id: number) => {
    try {
      await apiClient.post(`/evidences/${id}/reprocess`);
      // Recargamos evidencias y stats del schedule actual
      if (selectedScheduleId) {
        try {
          const [evidencesRes, statsRes] = await Promise.all([
            apiClient.get<{ data: { items: EvidenceItem[] } }>('/evidences', {
              params: { schedule_id: selectedScheduleId, pageSize: 50 },
            }),
            apiClient.get<{ data: { total: number; pending: number; error: number; processed: number } }>(
              '/evidences/stats',
              { params: { schedule_id: selectedScheduleId } }
            ),
          ]);
          setEvidences(evidencesRes.data.data.items);
          setEvidenceStats(statsRes.data.data);
          setEvidencesError(null);
        } catch (e) {
          // Si falla la recarga, dejamos la lista como está
        }
      }
    } catch (e) {
      setEvidencesError('No se pudo reencolar el procesamiento de la evidencia');
    }
  };

  const handleChecklistToggleComplete = async (item: ChecklistItem, completed: boolean) => {
    setChecklistItems(prev =>
      prev.map(it =>
        it.id === item.id
          ? {
              ...it,
              completed_at: completed ? Math.floor(Date.now() / 1000) : null,
              completed_by: completed ? it.completed_by : null,
            }
          : it,
      ),
    );

    try {
      await apiClient.patch(`/mobile/checklist-items/${item.id}`, {
        completed,
      });
    } catch (e) {
      setChecklistError('No se pudo actualizar el estado del ítem de checklist');
    }
  };

  const handleCreateChecklistItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedScheduleId || !newChecklistLabel.trim()) return;

    setChecklistLoading(true);
    setChecklistError(null);
    try {
      const res = await apiClient.post<{
        data: ChecklistItem;
      }>(`/mobile/schedules/${selectedScheduleId}/checklist`, {
        label: newChecklistLabel.trim(),
        is_required: newChecklistRequired,
        position:
          newChecklistPosition === '' ? undefined : Number.parseInt(String(newChecklistPosition), 10),
      });
      const created = res.data.data;
      setChecklistItems(prev =>
        [...prev, created].sort((a, b) => {
          if (a.position === b.position) return a.id - b.id;
          return a.position - b.position;
        }),
      );
      setNewChecklistLabel('');
      setNewChecklistRequired(false);
      setNewChecklistPosition('');
    } catch (e) {
      setChecklistError('No se pudo crear el ítem de checklist');
    } finally {
      setChecklistLoading(false);
    }
  };

  const loadOptions = async () => {
    try {
      const [projectsRes, usersRes, templatesRes] = await Promise.all([
        apiClient.get<PagedResponse<ProjectOption>>('/projects', {
          params: { per_page: 100 },
        }),
        apiClient.get<PagedResponse<UserOption>>('/users', {
          params: { per_page: 100 },
        }),
        apiClient.get<{ data: { items: any[] } }>('/audit-templates'),
      ]);

      setProjects(projectsRes.data.data.items);
      setUsers(usersRes.data.data.items);
      const rawTemplates = templatesRes.data.data.items || [];
      const mappedTemplates: TemplateOption[] = rawTemplates.map((t: any) => ({
        id: t.id,
        name: t.name,
        category: t.category,
        project_id: typeof t.project_id === 'number' ? t.project_id : t.project_id ? Number(t.project_id) : null,
      }));
      setTemplates(mappedTemplates);
    } catch (e) {
      // Opciones no críticas para funcionamiento del listado; no propagamos error al usuario.
    }
  };

  useEffect(() => {
    loadSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyMine]);

  useEffect(() => {
    loadOptions();
  }, []);

  // Cargar usuarios presentes en la auditoría seleccionada con un polling sencillo
  useEffect(() => {
    if (!selectedScheduleId) {
      setPresenceUsers([]);
      return;
    }

    let cancelled = false;

    const loadPresence = async () => {
      try {
        const res = await apiClient.get<{
          data: { schedule_id: number; users: { id: number; username: string; role: string }[] };
        }>('/realtime/presence', {
          params: { schedule_id: selectedScheduleId },
        });
        if (!cancelled) {
          setPresenceUsers(res.data.data.users || []);
        }
      } catch {
        if (!cancelled) {
          setPresenceUsers([]);
        }
      }
    };

    loadPresence();
    const interval = window.setInterval(loadPresence, 30000); // cada 30s

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [selectedScheduleId]);

  // Heartbeat: mientras haya una auditoría seleccionada, enviar join cada 60s
  useEffect(() => {
    if (!selectedScheduleId) return undefined;

    const sendHeartbeat = async () => {
      try {
        await apiClient.post('/realtime/presence', {
          schedule_id: selectedScheduleId,
          action: 'join',
        });
      } catch {
        // Ignoramos errores de heartbeat
      }
    };

    const interval = window.setInterval(sendHeartbeat, 60000);

    return () => {
      window.clearInterval(interval);
    };
  }, [selectedScheduleId]);

  const startCreate = () => {
    setError(null);
    setForm(emptyForm);
  };

  const startEdit = (item: ScheduleItem) => {
    setError(null);
    setForm({
      id: item.id,
      project_id: item.project_id,
      template_id: '',
      auditor_id: item.auditor_id,
      scheduled_start: item.scheduled_start ? item.scheduled_start.slice(0, 16) : '',
      scheduled_end: item.scheduled_end ? item.scheduled_end.slice(0, 16) : '',
      status: item.status,
    });
  };

  const selectScheduleForEvidences = async (item: ScheduleItem) => {
    // Registrar presencia: leave de la anterior, join de la nueva
    try {
      if (lastPresenceScheduleId && lastPresenceScheduleId !== item.id) {
        await apiClient.post('/realtime/presence', {
          schedule_id: lastPresenceScheduleId,
          action: 'leave',
        });
      }
      await apiClient.post('/realtime/presence', {
        schedule_id: item.id,
        action: 'join',
      });
      setLastPresenceScheduleId(item.id);
    } catch {
      // Si falla la presencia, no bloqueamos el resto de la UI
    }

    setSelectedScheduleId(item.id);
    setPresenceUsers([]);
    setEvidences([]);
    setEvidencesError(null);
    setEvidenceStats(null);
    setPdfs([]);
    setPdfsError(null);
    setUploadFile(null);
    setPdfMessage(null);
    setChecklistItems([]);
    setChecklistError(null);

    setEvidencesLoading(true);
    try {
      const res = await apiClient.get<{ data: { items: EvidenceItem[] } }>(
        '/evidences',
        { params: { schedule_id: item.id, pageSize: 50 } }
      );
      setEvidences(res.data.data.items);
    } catch (e) {
      setEvidencesError('No se pudieron cargar las evidencias');
    } finally {
      setEvidencesLoading(false);
    }

    try {
      const statsRes = await apiClient.get<{
        data: { total: number; pending: number; error: number; processed: number };
      }>('/evidences/stats', {
        params: { schedule_id: item.id },
      });
      setEvidenceStats(statsRes.data.data);
    } catch (e) {
      // Stats no críticas, las ignoramos si fallan
      setEvidenceStats(null);
    }

    setPdfsLoading(true);
    try {
      const pdfRes = await apiClient.get<{ data: { items: SchedulePdfItem[] } }>(
        `/schedules/${item.id}/pdfs`
      );
      setPdfs(pdfRes.data.data.items);
    } catch (e) {
      setPdfsError('No se pudieron cargar los PDFs de esta auditoría');
    } finally {
      setPdfsLoading(false);
    }

    setChecklistLoading(true);
    try {
      const checklistRes = await apiClient.get<{
        data: { items: ChecklistItem[] };
      }>(`/mobile/schedules/${item.id}/checklist`);
      const loadedItems = checklistRes.data.data.items || [];
      setChecklistItems(
        loadedItems.slice().sort((a, b) => {
          if (a.position === b.position) return a.id - b.id;
          return a.position - b.position;
        }),
      );
      setChecklistError(null);
    } catch (e) {
      setChecklistItems([]);
      setChecklistError('No se pudo cargar el checklist de la auditoría');
    } finally {
      setChecklistLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'scheduled_start') {
      const date = value ? value.substring(0, 10) : '';
      if (date) {
        apiClient
          .get<{ data: { items: { id: number; username: string; email: string; currentLoad: number; score: number }[] } }>(
            '/schedules/suggest-auditors',
            { params: { date } }
          )
          .then(res => {
            setAuditorSuggestions(res.data.data.items);
          })
          .catch(() => {
            setAuditorSuggestions([]);
          });
      } else {
        setAuditorSuggestions([]);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const scheduledDate = form.scheduled_start
      ? form.scheduled_start.substring(0, 10)
      : '';

    const timeFromInput = (value: string): string | null => {
      if (!value) return null;
      const timePart = value.substring(11, 16); // HH:MM
      if (!timePart) return null;
      return `${timePart}:00`;
    };

    const payload = {
      project_id:
        typeof form.project_id === 'string'
          ? parseInt(form.project_id, 10)
          : form.project_id,
      template_id:
        typeof form.template_id === 'string'
          ? (form.template_id === '' ? undefined : parseInt(form.template_id, 10))
          : form.template_id || undefined,
      auditor_id:
        typeof form.auditor_id === 'string'
          ? parseInt(form.auditor_id, 10)
          : form.auditor_id,
      scheduled_date: scheduledDate,
      scheduled_time_start: timeFromInput(form.scheduled_start),
      scheduled_time_end: timeFromInput(form.scheduled_end),
      status: form.status,
    };

    try {
      if (form.id == null) {
        await apiClient.post('/schedules', payload);
      } else {
        await apiClient.put(`/schedules/${form.id}`, payload);
      }
      setForm(emptyForm);
      await loadSchedules();
    } catch (e) {
      setError('Error al guardar la auditoría. Revisa fechas y solapamientos.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#020617',
        padding: '8px 8px 24px',
        color: '#e5e7eb',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
      }}
    >
      <header style={{ marginBottom: 16 }}>
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: 0.3,
            color: '#f9fafb',
          }}
        >
          Agenda de auditorías
        </h1>
        <p
          style={{
            margin: '4px 0 0',
            fontSize: 13,
            color: '#9ca3af',
          }}
        >
          Planifica y gestiona las auditorías, evidencias y presencia en tiempo real.
        </p>
      </header>

      <section style={{ marginBottom: 12 }}>
        <label
          style={{
            fontSize: 12,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <input
            type="checkbox"
            checked={onlyMine}
            onChange={e => setOnlyMine(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          Mostrar solo mi agenda
        </label>
      </section>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 2fr)',
          gap: 16,
          alignItems: 'flex-start',
        }}
      >
        {/* Formulario auditoría */}
        <section
          style={{
            padding: 16,
            borderRadius: 18,
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid rgba(148, 163, 184, 0.35)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 10,
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 600,
                color: '#e5e7eb',
              }}
            >
              {form.id == null ? 'Nueva auditoría' : `Editar auditoría #${form.id}`}
            </h2>
            <button
              type="button"
              onClick={startCreate}
              style={{
                padding: '6px 10px',
                borderRadius: 999,
                border: '1px solid rgba(148, 163, 184, 0.7)',
                backgroundColor: 'transparent',
                color: '#e5e7eb',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Nueva
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 10 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: 4,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Proyecto
              </label>
              <select
                name="project_id"
                value={form.project_id}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 12,
                  border: '1px solid rgba(148, 163, 184, 0.6)',
                  backgroundColor: 'rgba(15, 23, 42, 0.85)',
                  color: '#e5e7eb',
                  fontSize: 13,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              >
                <option value="">Selecciona un proyecto</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} (#{p.id})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 10 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: 4,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Plantilla (opcional)
              </label>
              <select
                name="template_id"
                value={form.template_id}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 12,
                  border: '1px solid rgba(148, 163, 184, 0.6)',
                  backgroundColor: 'rgba(15, 23, 42, 0.85)',
                  color: '#e5e7eb',
                  fontSize: 13,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              >
                <option value="">Sin plantilla</option>
                {templates
                  .filter(t =>
                    form.project_id === '' || t.project_id === null || t.project_id === form.project_id,
                  )
                  .map(t => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.category})
                    </option>
                  ))}
              </select>
            </div>

            <div style={{ marginBottom: 10 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: 4,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Auditor
              </label>
              <select
                name="auditor_id"
                value={form.auditor_id}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 12,
                  border: '1px solid rgba(148, 163, 184, 0.6)',
                  backgroundColor: 'rgba(15, 23, 42, 0.85)',
                  color: '#e5e7eb',
                  fontSize: 13,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              >
                <option value="">Selecciona un auditor</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.username} (#{u.id})
                  </option>
                ))}
              </select>
              {auditorSuggestions.length > 0 && (
                <div style={{ marginTop: 4, fontSize: 12 }}>
                  <strong>Sugerencias para esta fecha:</strong>
                  <ul style={{ paddingLeft: 16, marginTop: 4 }}>
                    {auditorSuggestions.map(s => (
                      <li key={s.id}>
                        <button
                          type="button"
                          style={{
                            padding: 0,
                            border: 'none',
                            background: 'none',
                            color: '#38bdf8',
                            cursor: 'pointer',
                          }}
                          onClick={() =>
                            setForm(prev => ({
                              ...prev,
                              auditor_id: s.id,
                            }))
                          }
                        >
                          {s.username} (#{s.id}) · carga: {s.currentLoad}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: 4,
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  Inicio
                </label>
                <input
                  type="datetime-local"
                  name="scheduled_start"
                  value={form.scheduled_start}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: 12,
                    border: '1px solid rgba(148, 163, 184, 0.6)',
                    backgroundColor: 'rgba(15, 23, 42, 0.85)',
                    color: '#e5e7eb',
                    fontSize: 13,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: 4,
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  Fin
                </label>
                <input
                  type="datetime-local"
                  name="scheduled_end"
                  value={form.scheduled_end}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: 12,
                    border: '1px solid rgba(148, 163, 184, 0.6)',
                    backgroundColor: 'rgba(15, 23, 42, 0.85)',
                    color: '#e5e7eb',
                    fontSize: 13,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: 10 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: 4,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Estado
              </label>
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 12,
                  border: '1px solid rgba(148, 163, 184, 0.6)',
                  backgroundColor: 'rgba(15, 23, 42, 0.85)',
                  color: '#e5e7eb',
                  fontSize: 13,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              >
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {error && (
              <div
                style={{
                  color: '#fecaca',
                  background: 'rgba(220, 38, 38, 0.12)',
                  border: '1px solid rgba(248, 113, 113, 0.4)',
                  borderRadius: 10,
                  padding: '6px 8px',
                  fontSize: 12,
                  marginBottom: 8,
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                marginTop: 4,
                padding: '8px 14px',
                borderRadius: 999,
                border: 'none',
                fontSize: 13,
                fontWeight: 600,
                cursor: loading ? 'default' : 'pointer',
                color: '#0b1120',
                background:
                  loading
                    ? 'linear-gradient(to right, #6b7280, #4b5563)'
                    : 'linear-gradient(135deg, #22c55e, #16a34a, #22c55e)',
                boxShadow: loading
                  ? '0 0 0 rgba(0,0,0,0)'
                  : '0 12px 30px rgba(22, 163, 74, 0.55)',
                opacity: loading ? 0.9 : 1,
              }}
            >
              {loading ? 'Guardando...' : 'Guardar auditoría'}
            </button>
          </form>
        </section>

        {/* Listado auditorías */}
        <section
          style={{
            padding: 16,
            borderRadius: 18,
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid rgba(148, 163, 184, 0.35)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 10,
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 600,
                color: '#e5e7eb',
              }}
            >
              Listado de auditorías
            </h2>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>Total: {items.length}</span>
          </div>

          {loading && <p style={{ fontSize: 12 }}>Cargando...</p>}
          {!loading && items.length === 0 && (
            <p style={{ fontSize: 12 }}>No hay auditorías.</p>
          )}
          {!loading && items.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr
                    style={{
                      textAlign: 'left',
                      borderBottom: '1px solid rgba(55, 65, 81, 0.8)',
                    }}
                  >
                    <th style={{ padding: '6px 4px' }}>ID</th>
                    <th style={{ padding: '6px 4px' }}>Proyecto</th>
                    <th style={{ padding: '6px 4px' }}>Auditor</th>
                    <th style={{ padding: '6px 4px' }}>Inicio</th>
                    <th style={{ padding: '6px 4px' }}>Fin</th>
                    <th style={{ padding: '6px 4px' }}>Estado</th>
                    <th style={{ padding: '6px 4px' }}>Riesgo</th>
                    <th style={{ padding: '6px 4px' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr
                      key={item.id}
                      style={{ borderBottom: '1px solid rgba(31, 41, 55, 0.9)' }}
                    >
                      <td style={{ padding: '6px 4px' }}>{item.id}</td>
                      <td style={{ padding: '6px 4px' }}>
                        {item.project_name || `#${item.project_id}`}
                      </td>
                      <td style={{ padding: '6px 4px' }}>
                        {item.auditor_username || `#${item.auditor_id}`}
                      </td>
                      <td style={{ padding: '6px 4px' }}>
                        {new Date(item.scheduled_start).toLocaleString()}
                      </td>
                      <td style={{ padding: '6px 4px' }}>
                        {new Date(item.scheduled_end).toLocaleString()}
                      </td>
                      <td style={{ padding: '6px 4px' }}>{item.status}</td>
                      <td style={{ padding: '6px 4px' }}>
                        {(() => {
                          if (item.risk_score == null) return '-';
                          const score = item.risk_score;
                          let label = 'Bajo';
                          let color = 'green';
                          if (score >= 67) {
                            label = 'Alto';
                            color = 'red';
                          } else if (score >= 34) {
                            label = 'Medio';
                            color = 'orange';
                          }
                          return (
                            <span style={{ color }}>
                              {label} ({score})
                            </span>
                          );
                        })()}
                      </td>
                      <td style={{ padding: '6px 4px' }}>
                        <button
                          type="button"
                          onClick={() => startEdit(item)}
                          style={{
                            padding: '4px 8px',
                            borderRadius: 999,
                            border: '1px solid rgba(148, 163, 184, 0.7)',
                            backgroundColor: 'transparent',
                            color: '#e5e7eb',
                            fontSize: 11,
                            cursor: 'pointer',
                            marginRight: 4,
                          }}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => selectScheduleForEvidences(item)}
                          style={{
                            padding: '4px 8px',
                            borderRadius: 999,
                            border: '1px solid rgba(59, 130, 246, 0.7)',
                            backgroundColor: 'rgba(30, 64, 175, 0.35)',
                            color: '#bfdbfe',
                            fontSize: 11,
                            cursor: 'pointer',
                          }}
                        >
                          Evidencias
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {/* Evidencias y actividad de la auditoría seleccionada */}
      {selectedScheduleId && (
        <section
          style={{
            marginTop: 24,
            padding: 16,
            borderRadius: 18,
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid rgba(148, 163, 184, 0.35)',
          }}
        >
          <h2
            style={{
              margin: '0 0 10px',
              fontSize: 15,
              fontWeight: 600,
              color: '#e5e7eb',
            }}
          >
            Evidencias de la auditoría #{selectedScheduleId}
          </h2>
          {presenceUsers.length > 0 && (
            <div style={{ marginBottom: 8, fontSize: 12 }}>
              <strong>Usuarios conectados ahora mismo:</strong>{' '}
              {presenceUsers.map(u => u.username).join(', ')}
            </div>
          )}
          {evidenceStats && (
            <div style={{ marginBottom: 8, fontSize: 12 }}>
              <strong>Resumen evidencias:</strong>{' '}
              total {evidenceStats.total} · pendientes {evidenceStats.pending} · con error{' '}
              {evidenceStats.error} · procesadas {evidenceStats.processed}
            </div>
          )}
          <div style={{ marginBottom: 8 }}>
            <button
              type="button"
              onClick={handleGeneratePdf}
              disabled={pdfLoading}
              style={{
                padding: '6px 10px',
                borderRadius: 999,
                border: 'none',
                fontSize: 12,
                fontWeight: 500,
                cursor: pdfLoading ? 'default' : 'pointer',
                color: '#0b1120',
                background:
                  pdfLoading
                    ? 'linear-gradient(to right, #6b7280, #4b5563)'
                    : 'linear-gradient(135deg, #22c55e, #16a34a, #22c55e)',
              }}
            >
              {pdfLoading ? 'Generando PDF...' : 'Generar PDF de auditoría'}
            </button>
          </div>
          {pdfMessage && (
            <div
              style={{
                marginBottom: 8,
                color: pdfMessage.includes('No se pudo') ? '#fecaca' : '#bbf7d0',
                fontSize: 12,
              }}
            >
              {pdfMessage}
            </div>
          )}
          <div style={{ marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 13 }}>PDFs generados</h3>
            {pdfsLoading && <p style={{ fontSize: 12 }}>Cargando PDFs...</p>}
            {pdfsError && <p style={{ color: '#fecaca', fontSize: 12 }}>{pdfsError}</p>}
            {!pdfsLoading && !pdfsError && pdfs.length === 0 && (
              <p style={{ fontSize: 12 }}>No hay PDFs generados para esta auditoría.</p>
            )}
            {!pdfsLoading && !pdfsError && pdfs.length > 0 && (
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 12,
                  marginTop: 6,
                }}
              >
                <thead>
                  <tr
                    style={{
                      textAlign: 'left',
                      borderBottom: '1px solid rgba(55, 65, 81, 0.8)',
                    }}
                  >
                    <th style={{ padding: '6px 4px' }}>ID</th>
                    <th style={{ padding: '6px 4px' }}>Fecha</th>
                    <th style={{ padding: '6px 4px' }}>Generado por</th>
                    <th style={{ padding: '6px 4px' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {pdfs.map(pdf => (
                    <tr
                      key={pdf.id}
                      style={{ borderBottom: '1px solid rgba(31, 41, 55, 0.9)' }}
                    >
                      <td style={{ padding: '6px 4px' }}>{pdf.id}</td>
                      <td style={{ padding: '6px 4px' }}>
                        {new Date(pdf.created_at * 1000).toLocaleString()}
                      </td>
                      <td style={{ padding: '6px 4px' }}>{pdf.created_by}</td>
                      <td style={{ padding: '6px 4px' }}>
                        <a
                          href={pdf.url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: '#38bdf8', textDecoration: 'none' }}
                        >
                          Ver/descargar PDF
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <form onSubmit={handleUploadEvidence} style={{ marginBottom: 12 }}>
            <input type="file" onChange={handleFileChange} />{' '}
            <button
              type="submit"
              disabled={uploadLoading || !uploadFile}
              style={{
                padding: '6px 10px',
                borderRadius: 999,
                border: 'none',
                fontSize: 12,
                fontWeight: 500,
                cursor: uploadLoading || !uploadFile ? 'default' : 'pointer',
                color: '#0b1120',
                background:
                  uploadLoading || !uploadFile
                    ? 'linear-gradient(to right, #6b7280, #4b5563)'
                    : 'linear-gradient(135deg, #22c55e, #16a34a, #22c55e)',
              }}
            >
              {uploadLoading ? 'Subiendo...' : 'Subir evidencia'}
            </button>
          </form>
          {evidencesError && (
            <div style={{ color: '#fecaca', marginBottom: 8, fontSize: 12 }}>
              {evidencesError}
            </div>
          )}
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 12 }}>
              Filtro evidencias:{' '}
              <select
                value={evidencesFilter}
                onChange={e => setEvidencesFilter(e.target.value as 'all' | 'pending' | 'error')}
                style={{
                  padding: '4px 8px',
                  borderRadius: 999,
                  border: '1px solid rgba(148, 163, 184, 0.6)',
                  backgroundColor: 'rgba(15, 23, 42, 0.95)',
                  color: '#e5e7eb',
                  fontSize: 12,
                }}
              >
                <option value="all">Todas</option>
                <option value="pending">Solo pendientes</option>
                <option value="error">Solo con error</option>
              </select>
            </label>
          </div>
          {evidencesLoading && <p style={{ fontSize: 12 }}>Cargando evidencias...</p>}
          {!evidencesLoading && evidences.length === 0 && (
            <p style={{ fontSize: 12 }}>No hay evidencias para esta auditoría.</p>
          )}
          {!evidencesLoading && evidences.length > 0 && (
            (() => {
              const filtered = evidences.filter(ev => {
                if (evidencesFilter === 'pending') {
                  return !ev.processed_at;
                }
                if (evidencesFilter === 'error') {
                  return !!ev.last_error;
                }
                return true;
              });
              if (filtered.length === 0) {
                return (
                  <p style={{ fontSize: 12 }}>
                    No hay evidencias que coincidan con el filtro.
                  </p>
                );
              }
              return (
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 12,
                    marginTop: 8,
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        textAlign: 'left',
                        borderBottom: '1px solid rgba(55, 65, 81, 0.8)',
                      }}
                    >
                      <th style={{ padding: '6px 4px' }}>ID</th>
                      <th style={{ padding: '6px 4px' }}>Nombre</th>
                      <th style={{ padding: '6px 4px' }}>MIME</th>
                      <th style={{ padding: '6px 4px' }}>Tamaño</th>
                      <th style={{ padding: '6px 4px' }}>Procesado</th>
                      <th style={{ padding: '6px 4px' }}>Intentos</th>
                      <th style={{ padding: '6px 4px' }}>Error</th>
                      <th style={{ padding: '6px 4px' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(ev => (
                      <tr
                        key={ev.id}
                        style={{ borderBottom: '1px solid rgba(31, 41, 55, 0.9)' }}
                      >
                        <td style={{ padding: '6px 4px' }}>{ev.id}</td>
                        <td style={{ padding: '6px 4px' }}>{ev.original_name}</td>
                        <td style={{ padding: '6px 4px' }}>{ev.mime_type ?? '-'}</td>
                        <td style={{ padding: '6px 4px' }}>
                          {ev.size != null ? `${(ev.size / 1024).toFixed(1)} KB` : '-'}
                        </td>
                        <td style={{ padding: '6px 4px' }}>
                          {ev.processed_at
                            ? new Date(ev.processed_at * 1000).toLocaleString()
                            : 'Pendiente'}
                        </td>
                        <td style={{ padding: '6px 4px' }}>{ev.processing_attempts}</td>
                        <td
                          style={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            padding: '6px 4px',
                          }}
                        >
                          {ev.last_error ?? '-'}
                        </td>
                        <td style={{ padding: '6px 4px' }}>
                          <a
                            href={ev.url}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: '#38bdf8', textDecoration: 'none', marginRight: 4 }}
                          >
                            Ver/descargar
                          </a>
                          <button
                            type="button"
                            onClick={() => handleDeleteEvidence(ev.id)}
                            style={{
                              padding: '4px 8px',
                              borderRadius: 999,
                              border: '1px solid rgba(248, 113, 113, 0.6)',
                              backgroundColor: 'rgba(127, 29, 29, 0.2)',
                              color: '#fecaca',
                              fontSize: 11,
                              cursor: 'pointer',
                            }}
                          >
                            Eliminar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()
          )}

          <div
            style={{
              marginTop: 24,
              paddingTop: 12,
              borderTop: '1px solid rgba(55, 65, 81, 0.8)',
            }}
          >
            <h3 style={{ margin: '0 0 8px', fontSize: 13 }}>Checklist de la auditoría</h3>
            {checklistError && (
              <div style={{ color: '#fecaca', marginBottom: 8, fontSize: 12 }}>{checklistError}</div>
            )}
            {checklistLoading && <p style={{ fontSize: 12 }}>Cargando checklist...</p>}
            {!checklistLoading && checklistItems.length === 0 && (
              <p style={{ fontSize: 12 }}>No hay ítems de checklist para esta auditoría.</p>
            )}
            {!checklistLoading && checklistItems.length > 0 && (
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 8px' }}>
                {checklistItems.map(item => {
                  const completed = !!item.completed_at;
                  return (
                    <li
                      key={item.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '4px 0',
                        fontSize: 12,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={completed}
                        onChange={e => handleChecklistToggleComplete(item, e.target.checked)}
                      />
                      <span style={{ flex: 1 }}>
                        {item.label}
                        {item.is_required && <span style={{ color: '#f97316' }}> · obligatorio</span>}
                      </span>
                      {item.note && (
                        <span
                          style={{
                            maxWidth: 220,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            color: '#9ca3af',
                          }}
                        >
                          Nota: {item.note}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            <form onSubmit={handleCreateChecklistItem} style={{ marginTop: 8, fontSize: 12 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                <input
                  type="text"
                  placeholder="Nuevo ítem de checklist"
                  value={newChecklistLabel}
                  onChange={e => setNewChecklistLabel(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '6px 8px',
                    borderRadius: 999,
                    border: '1px solid rgba(148, 163, 184, 0.6)',
                    backgroundColor: 'rgba(15, 23, 42, 0.95)',
                    color: '#e5e7eb',
                  }}
                />
                <input
                  type="number"
                  placeholder="Posición"
                  value={newChecklistPosition}
                  onChange={e => {
                    const v = e.target.value;
                    setNewChecklistPosition(v === '' ? '' : Number.parseInt(v, 10) || '');
                  }}
                  style={{
                    width: 80,
                    padding: '6px 8px',
                    borderRadius: 999,
                    border: '1px solid rgba(148, 163, 184, 0.6)',
                    backgroundColor: 'rgba(15, 23, 42, 0.95)',
                    color: '#e5e7eb',
                  }}
                />
              </div>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
                <input
                  type="checkbox"
                  checked={newChecklistRequired}
                  onChange={e => setNewChecklistRequired(e.target.checked)}
                />
                Obligatorio
              </label>
              <div>
                <button
                  type="submit"
                  disabled={checklistLoading || !newChecklistLabel.trim()}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 999,
                    border: 'none',
                    fontSize: 12,
                    fontWeight: 500,
                    cursor:
                      checklistLoading || !newChecklistLabel.trim() ? 'default' : 'pointer',
                    color: '#0b1120',
                    background:
                      checklistLoading || !newChecklistLabel.trim()
                        ? 'linear-gradient(to right, #6b7280, #4b5563)'
                        : 'linear-gradient(135deg, #22c55e, #16a34a, #22c55e)',
                  }}
                >
                  {checklistLoading ? 'Guardando ítem...' : 'Añadir ítem'}
                </button>
              </div>
            </form>
          </div>
        </section>
      )}
    </div>
  );
}

export default SchedulesPage;
